/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { Role } from 'shared/types/role';
import { getBirthDay, getFullName } from 'shared/utils';

import { AppDispatch } from 'store/store';
import { UDSelect } from 'ud-form/components';
import { UDButton, UDFilter, UDList, UDListItem, UDText } from 'ud-ui/components';
import { TournamentUser } from '../../../../domain/interfaces/tournament-user';
import { changePlayerRole, deleteTournamentPlayerApplication } from '../../../../store/players/actions';
import { ParticipantsList } from './styles';

interface Props {
  teamId: number;
  participants?: TournamentUser[];
}

const TeamsComponentsFormParticipantsListPart: React.FC<Props> = ({ teamId, participants = [] }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');

  const changeRole = useCallback(
    (participant: TournamentUser, role: string) => {
      try {
        dispatch(
          changePlayerRole({
            teamId,
            userId: participant.id,
            role: role as Role,
          }),
        )
          .unwrap()
          .then(() => {
            toast.success(
              t('participant.changeRole', {
                participant: getFullName(participant),
                role: t(`player.role.${role}`),
              }),
            );
          })
          .catch(() => {
            toast.error(
              t('participant.changeRoleError', {
                participant: getFullName(participant),
              }),
            );
          });
      } catch (error) {
        toast.error(
          t('participant.changeRoleError', {
            participant: getFullName(participant),
          }),
        );
      }
    },
    [dispatch, t, teamId],
  );

  const kickFromTeam = useCallback(
    (participant: TournamentUser) => {
      try {
        dispatch(deleteTournamentPlayerApplication({ teamId, userId: participant.id }))
          .unwrap()
          .then(() => {
            toast.success(
              t('participant.kickFromTeam', {
                participant: getFullName(participant),
              }),
            );
          })
          .catch(() => {
            toast.error(
              t('participant.kickFromTeamError', {
                participant: getFullName(participant),
              }),
            );
          });
      } catch (error) {
        toast.error(
          t('participant.kickFromTeamError', {
            participant: getFullName(participant),
          }),
        );
      }
    },
    [dispatch, t, teamId],
  );

  const participantsByFilter = useMemo(() => {
    return participants.filter((participant) => {
      return getFullName(participant).toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [participants, filterValue]);

  return (
    <ParticipantsList>
      <UDFilter
        inputPlaceholder={t('participant.filter.inputPlaceholder')}
        value={filterValue}
        onChange={setFilterValue}
        style={{ marginBottom: 10 }}
      />

      <UDList style={{ overflow: 'unset', minHeight: 'calc(100vh - 314px)' }}>
        {participantsByFilter.length > 0 ? (
          participantsByFilter.map((participant, index) => (
            <UDListItem
              key={participant.id}
              index={index + 1}
              image={participant.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
              title={getFullName(participant)}
              subtitle={getBirthDay({
                birthDate: new Date(participant?.birthDate ?? ''),
              })}
              additionalFields={[
                <div key={participant.id} className="flex-center" style={{ marginLeft: 'auto', paddingLeft: 10 }}>
                  <UDSelect
                    divProps={{ style: { width: 144, marginRight: 30 } }}
                    options={Roles.map((role) => ({
                      label: t(`player.role.${role}`),
                      value: role,
                    }))}
                    value={{
                      label: t(`player.role.${participant.role}`),
                      value: participant.role,
                    }}
                    onChange={(value) => changeRole(participant, value?.value ?? '')}
                  />

                  <UDButton style={{ width: 144 }} variant="secondary" onClick={() => kickFromTeam(participant)}>
                    {t('participant.remove')}
                  </UDButton>
                </div>,
              ]}
            />
          ))
        ) : (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100%;
              min-height: calc(100vh - 314px);
            `}
          >
            <UDText type="subhead" className="bold" color="#8F8E99" style={{ fontWeight: 400, fontSize: 16 }}>
              {t('participant.empty')}
            </UDText>
          </div>
        )}
      </UDList>
    </ParticipantsList>
  );
};

export default TeamsComponentsFormParticipantsListPart;

const Roles = ['STAFF', 'CAPTAIN', 'FAN'];
